import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Alert,
  CircularProgress,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Refresh as RefreshIcon,
  FilterList as FilterListIcon,
  Lock as LockIcon,
  Upgrade as UpgradeIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { apiClient, FalsePositiveReport, BillingMeResponse } from '../../services/api';
import { format } from 'date-fns';

const statusColors = {
  pending: 'warning',
  reviewed: 'info',
  accepted: 'success',
  rejected: 'error',
} as const;

const reasonLabels = {
  not_applicable: 'Not Applicable',
  incorrect_detection: 'Incorrect Detection',
  environment_specific: 'Environment-Specific',
  other: 'Other',
} as const;

export function FalsePositiveReports() {
  const navigate = useNavigate();
  const [reports, setReports] = useState<FalsePositiveReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [billing, setBilling] = useState<BillingMeResponse | null>(null);
  const [isLocked, setIsLocked] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedReport, setSelectedReport] = useState<FalsePositiveReport | null>(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [reportToDelete, setReportToDelete] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch billing info to check plan
      const billingData = await apiClient.getMyBilling();
      setBilling(billingData);

      // Check if user is on Free plan
      const planId = billingData.plan?.id?.toLowerCase();
      const locked = planId === 'free';
      setIsLocked(locked);

      // Only fetch reports if not locked
      if (!locked) {
        const params = statusFilter !== 'all' ? { status: statusFilter } : {};
        const response = await apiClient.getFalsePositiveReports(params);
        setReports(response.data || []);
      }
    } catch (err: any) {
      console.error('Error fetching false positive reports:', err);
      setError(err.response?.data?.message || 'Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const handleViewDetails = (report: FalsePositiveReport) => {
    setSelectedReport(report);
    setDetailsDialogOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsDialogOpen(false);
    setSelectedReport(null);
  };

  const handleDeleteClick = (reportId: string) => {
    setReportToDelete(reportId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!reportToDelete) return;

    try {
      setDeleting(true);
      await apiClient.deleteFalsePositiveReport(reportToDelete);
      setReports(reports.filter(r => r.id !== reportToDelete));
      setDeleteDialogOpen(false);
      setReportToDelete(null);
    } catch (err: any) {
      console.error('Error deleting report:', err);
      alert(err.response?.data?.message || 'Failed to delete report');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setReportToDelete(null);
  };

  const handleViewScan = (scanId: string) => {
    // Navigate to scans page - you might need to adjust this based on your routing
    navigate(`/scans`);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Show locked state for Free plan users
  if (isLocked) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>
          False Positive Reports
        </Typography>
        
        <Card
          sx={{
            position: 'relative',
            minHeight: '500px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            overflow: 'hidden',
          }}
        >
          {/* Blurred background pattern */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              opacity: 0.1,
              background: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,.05) 10px, rgba(255,255,255,.05) 20px)',
            }}
          />

          <CardContent
            sx={{
              position: 'relative',
              zIndex: 1,
              textAlign: 'center',
              maxWidth: '600px',
              mx: 'auto',
              py: 6,
            }}
          >
            {/* Lock Icon */}
            <Box
              sx={{
                display: 'inline-flex',
                p: 3,
                borderRadius: '50%',
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                mb: 3,
              }}
            >
              <LockIcon sx={{ fontSize: 60, color: 'white' }} />
            </Box>

            {/* Title */}
            <Typography variant="h4" gutterBottom fontWeight="bold">
              False Positive Reporting Locked
            </Typography>

            {/* Description */}
            <Typography variant="body1" sx={{ mb: 4, opacity: 0.9 }}>
              False positive reporting is a premium feature available on paid plans. 
              Upgrade your plan to report and track false positives, helping us improve 
              the accuracy of our security scanner.
            </Typography>

            {/* Features List */}
            <Box sx={{ mb: 4, textAlign: 'left', maxWidth: '400px', mx: 'auto' }}>
              <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                With False Positive Reporting, you can:
              </Typography>
              <ul style={{ paddingLeft: '20px', margin: '8px 0' }}>
                <li>Report incorrect detections</li>
                <li>Track report status and reviews</li>
                <li>Help improve scanner accuracy</li>
                <li>Reduce noise in your scan results</li>
              </ul>
            </Box>

            {/* Plan Info */}
            <Chip
              label={`Current Plan: ${billing?.plan?.name || 'Free'}`}
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                color: 'white',
                mb: 3,
                fontWeight: 'bold',
              }}
            />

            {/* Upgrade Button */}
            <Button
              variant="contained"
              size="large"
              startIcon={<UpgradeIcon />}
              onClick={() => navigate('/pricing')}
              sx={{
                bgcolor: 'white',
                color: '#667eea',
                fontWeight: 'bold',
                px: 4,
                py: 1.5,
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.9)',
                },
              }}
            >
              Upgrade to Unlock
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
            False Positive Reports
          </Typography>
          <Typography variant="body1" color="text.secondary">
            View and manage your reported false positives
          </Typography>
        </Box>
        <Button
          startIcon={<RefreshIcon />}
          onClick={fetchData}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {/* Filter */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" spacing={2} alignItems="center">
            <FilterListIcon color="action" />
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Status Filter</InputLabel>
              <Select
                value={statusFilter}
                label="Status Filter"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All Reports</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="reviewed">Reviewed</MenuItem>
                <MenuItem value="accepted">Accepted</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </Select>
            </FormControl>
            <Typography variant="body2" color="text.secondary">
              {reports.length} report{reports.length !== 1 ? 's' : ''} found
            </Typography>
          </Stack>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Reports Table */}
      {reports.length === 0 ? (
        <Card>
          <CardContent>
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No Reports Found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {statusFilter !== 'all' 
                  ? `No ${statusFilter} reports to display.`
                  : 'You haven\'t reported any false positives yet.'}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Rule ID</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell>Scan ID</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Reported</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reports.map((report) => (
                  <TableRow key={report.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {report.ruleId}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {reasonLabels[report.reason]}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => handleViewScan(report.scanId)}
                      >
                        {report.scanId}
                      </Button>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={report.status}
                        size="small"
                        color={statusColors[report.status]}
                        sx={{ textTransform: 'capitalize' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {format(new Date(report.createdAt), 'MMM dd, yyyy')}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => handleViewDetails(report)}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {report.status === 'pending' && (
                        <Tooltip title="Delete Report">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteClick(report.id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      {/* Details Dialog */}
      <Dialog open={detailsDialogOpen} onClose={handleCloseDetails} maxWidth="md" fullWidth>
        <DialogTitle>False Positive Report Details</DialogTitle>
        <DialogContent>
          {selectedReport && (
            <Box sx={{ pt: 1 }}>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Rule ID
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {selectedReport.ruleId}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Scan ID
                  </Typography>
                  <Typography variant="body1">
                    {selectedReport.scanId}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Reason
                  </Typography>
                  <Typography variant="body1">
                    {reasonLabels[selectedReport.reason]}
                  </Typography>
                </Box>

                {selectedReport.description && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Description
                    </Typography>
                    <Typography variant="body1">
                      {selectedReport.description}
                    </Typography>
                  </Box>
                )}

                {selectedReport.context && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Context
                    </Typography>
                    <Box sx={{ pl: 2 }}>
                      {selectedReport.context.file && (
                        <Typography variant="body2">
                          <strong>File:</strong> {selectedReport.context.file}
                        </Typography>
                      )}
                      {selectedReport.context.line && (
                        <Typography variant="body2">
                          <strong>Line:</strong> {selectedReport.context.line}
                          {selectedReport.context.column && `, Column: ${selectedReport.context.column}`}
                        </Typography>
                      )}
                      {selectedReport.context.framework && (
                        <Typography variant="body2">
                          <strong>Framework:</strong> {selectedReport.context.framework}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}

                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Status
                  </Typography>
                  <Box>
                    <Chip
                      label={selectedReport.status}
                      size="small"
                      color={statusColors[selectedReport.status]}
                      sx={{ textTransform: 'capitalize' }}
                    />
                  </Box>
                </Box>

                {selectedReport.reviewNotes && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Review Notes
                    </Typography>
                    <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                      <Typography variant="body2">
                        {selectedReport.reviewNotes}
                      </Typography>
                    </Paper>
                  </Box>
                )}

                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Reported On
                  </Typography>
                  <Typography variant="body2">
                    {format(new Date(selectedReport.createdAt), 'MMMM dd, yyyy HH:mm')}
                  </Typography>
                </Box>

                {selectedReport.reviewedAt && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Reviewed On
                    </Typography>
                    <Typography variant="body2">
                      {format(new Date(selectedReport.reviewedAt), 'MMMM dd, yyyy HH:mm')}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetails}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Delete Report?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this false positive report? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

